/*
    The signature pad's drawing model, free of React so it can be tested. Points are stored as shares
    of the pad's width and height: resizing a canvas clears it, and normalised strokes can be drawn
    again at any size - after a rotation, and for the exported PNG. No timing or pressure is kept;
    used to identify someone, that would be biometric data.
*/

export type SignaturePoint = {x: number, y: number};

export type SignatureStroke = SignaturePoint[];

export interface PadSize {
    width: number
    height: number
}

// The pad is paper whatever the hotel theme, so what the guest sees is what the PNG holds
export const PAD_PAPER = '#FFFFFF';
export const PAD_INK = '#1B1F3B';

// Enough ink: the strokes add up to this share of the pad's width. Stops taps and dots, not short names.
export const MIN_INK_RATIO = 0.4;

// A signature on white compresses to a few dozen kB; sign accepts up to 1 MB
export const EXPORT_MAX_WIDTH = 1000;
export const EXPORT_MAX_BYTES = 900 * 1024;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/** The point under a pointer as shares of [rect], clamped: a captured pointer can leave the pad. */
export const pointIn = (
    clientX: number,
    clientY: number,
    rect: {left: number, top: number, width: number, height: number}
): SignaturePoint => ({
    x: clamp01((clientX - rect.left) / (rect.width || 1)),
    y: clamp01((clientY - rect.top) / (rect.height || 1)),
});

/** Total length of the strokes in pixels at [size]. */
export const inkLength = (strokes: SignatureStroke[], {width, height}: PadSize): number => strokes.reduce(
    (total, stroke) => total + stroke.slice(1).reduce((length, point, index) => (
        length + Math.hypot((point.x - stroke[index].x) * width, (point.y - stroke[index].y) * height)
    ), 0),
    0
);

export const hasEnoughInk = (strokes: SignatureStroke[], size: PadSize): boolean => (
    size.width > 0 && inkLength(strokes, size) >= size.width * MIN_INK_RATIO
);

export const lineWidthFor = (width: number): number => Math.min(4, Math.max(2.5, width / 200));

/** Draws [strokes] in pad pixels; the caller's transform maps them to canvas pixels. */
export const drawStrokes = (
    context: CanvasRenderingContext2D,
    strokes: SignatureStroke[],
    {width, height}: PadSize,
    lineWidth = lineWidthFor(width)
) => {
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = lineWidth;
    context.strokeStyle = PAD_INK;
    context.fillStyle = PAD_INK;

    strokes.forEach(stroke => {
        if (!stroke.length) return;
        context.beginPath();

        // A tap is a dot
        if (stroke.length === 1) {
            context.arc(stroke[0].x * width, stroke[0].y * height, lineWidth / 2, 0, Math.PI * 2);
            context.fill();
            return;
        }

        stroke.forEach((point, index) => {
            if (index === 0) context.moveTo(point.x * width, point.y * height);
            else context.lineTo(point.x * width, point.y * height);
        });
        context.stroke();
    });
};

/** Clears the canvas and draws every stroke again at [size], sharp at [pixelRatio]. */
export const redrawPad = (
    context: CanvasRenderingContext2D,
    strokes: SignatureStroke[],
    size: PadSize,
    pixelRatio: number
) => {
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, size.width, size.height);
    drawStrokes(context, strokes, size);
};

/** The PNG's scale against the pad: up to twice its size, at most [maxWidth] pixels wide. */
export const exportScale = (width: number, maxWidth = EXPORT_MAX_WIDTH): number => (
    width > 0 ? Math.min(2, maxWidth / width) : 0
);

/** The strokes, dark on white, as a PNG data URL; undefined while there are none. */
export const exportPng = (
    strokes: SignatureStroke[],
    size: PadSize,
    createCanvas: () => HTMLCanvasElement = () => document.createElement('canvas')
): string | undefined => {
    if (!strokes.length || !size.width || !size.height) return undefined;

    const render = (scale: number) => {
        const canvas = createCanvas();
        canvas.width = Math.max(1, Math.round(size.width * scale));
        canvas.height = Math.max(1, Math.round(size.height * scale));
        const context = canvas.getContext('2d');
        if (!context) return undefined;

        context.fillStyle = PAD_PAPER;
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.setTransform(scale, 0, 0, scale, 0, 0);
        drawStrokes(context, strokes, size);
        return canvas.toDataURL('image/png');
    };

    const scale = exportScale(size.width);
    const png = render(scale);
    // Base64 carries 3 bytes in 4 characters; the half-size retry only guards sign's limit
    return png && png.length * 0.75 > EXPORT_MAX_BYTES ? render(scale / 2) : png;
};
